import React, { useRef, useState } from "react";
import _ from "lodash";
import { Search } from "lucide-react";
import { useHotkeys } from "react-hotkeys-hook";

import { Button } from "@/design-system/components/Button/Button";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { Input } from "@/design-system/components/Input/Input";

export function FormAccessSelector({
  onSelect,
  onSelectAll,
  hasFullAccess,
  allForms,
  selectedFormSubmissionIDs,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const focusSearch = (e: KeyboardEvent) => {
    e.preventDefault();
    searchInputRef.current?.focus();
  };
  useHotkeys("ctrl+f", focusSearch);
  useHotkeys("cmd+f", focusSearch);

  let filteredForms = React.useMemo(
    () => _.sortBy([...allForms], (f) => f.name.toLowerCase()),
    [allForms],
  );

  filteredForms.sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );

  const hasAccessToFormSubmissions = (formID) => {
    if (hasFullAccess) {
      return true;
    }

    return selectedFormSubmissionIDs.includes(formID);
  };

  const trimmedSearch = searchTerm.trim();
  if (trimmedSearch) {
    filteredForms = _.filter(filteredForms, (form) =>
      form.name.toLowerCase().includes(trimmedSearch.toLowerCase()),
    );
  }

  return (
    <div>
      <p className="font-bold">
        Form Submissions ({selectedFormSubmissionIDs.length}/{allForms.length})
      </p>
      <p className="text-textSubtle pb-2.5 text-xs">
        This teammate will be able to read form submissions for the selected
        forms
      </p>
      <div className="flex flex-col gap-2">
        <Input
          name="search"
          type="search"
          autoComplete="off"
          placeholder="Search"
          leadingIcon={<Search className="text-iconDefault" size={16} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          ref={searchInputRef}
        />
        <Button
          className="self-end"
          variant="link"
          onClick={() => {
            onSelectAll();
          }}
        >
          Select all
        </Button>
        <div className="flex w-full flex-col gap-2 text-sm">
          {filteredForms?.map((form) => {
            return (
              <div className="flex items-center justify-between" key={form.id}>
                <span className="font-medium">{form.name}</span>
                <Checkbox
                  checked={hasAccessToFormSubmissions(form.id)}
                  onCheckedChange={() => {
                    onSelect(form.id);
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
